import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Subject } from 'rxjs';
import { TablepageBaseComponent } from '../../abstract/tablepage-base/tablepage-base.component';
import { UserRowComponent } from '../../components/user-row/user-row.component';
import { UserDialogComponent } from '../../dialogs/user-dialog/user-dialog.component';
import { Role } from '../../enums/role';
import { User } from '../../models/user';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { Functions } from '../../utilities/functions';
import { Parameters } from '../../utilities/parameters';
import { Reset } from '../../utilities/reset';

@Component({
  selector: 'app-users',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    UserDialogComponent,
    TablepageBaseComponent,
    TranslatePipe,
    UserRowComponent
  ],
  standalone: true,
  templateUrl: './users.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class UsersComponent implements OnInit, OnDestroy {
  messageService = inject(UiNotificationService);

  loading: boolean = false;
  dtTrigger: Subject<boolean> = new Subject<boolean>();

  users: User[] = [];
  newUser: User = Reset.user();
  selectedUser: User = Reset.user();

  showDialog: boolean = false;
  isNew: boolean = false;


  isShowing: boolean = false;
  maxRole: number = Role.patient;
  constructor(private apiService: ApiService) {

  }

  ngOnInit(): void {
    this.maxRole = Functions.getMaxRoleUsable(Parameters.loggedUser.roles);
    this.loadData();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }

  isValid(date: string | Date): boolean {
    const threshold = new Date(2000, 0, 1); // 01/01/2000
    const inputDate = new Date(date);
    return inputDate > threshold;
  }

  onDelete(user: User): void {
    this.messageService.confirm('messages.userDisabled',
      () => {
        this.apiService.deleteUser(user).subscribe({
          next: () => {
            this.messageService.showSuccess('messages.success')
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.showError(err);
          }
        });
      },
      { labelKey: 'disable', severity: 'danger' }, 'confirm', 'pi-trash',
      { id: user.userId, name: user.userName }
    );
  }

  onUserSaved(user: User) {
    this.resetSelected();
    this.loadData();
  }

  onShowDialog(user: User): void {
    this.isShowing = true;

    this.isNew = user.userId === '';
    this.selectedUser = structuredClone(user);
    if (this.selectedUser.consents === undefined) {
      this.selectedUser.consents = Reset.userConsent();
    }

    if (!this.isNew) {
      this.apiService.getUserRelations(user.userId).subscribe({
        next: (r) => {
          this.selectedUser.organizations = r;

          this.apiService.getUserConsents(user.userId).subscribe({
            next: (c) => {
              this.selectedUser.consents = c;
              this.dtTrigger.next(true);
              this.showDialog = true;

              this.isShowing = false;
            },
            error: (err: HttpErrorResponse) => {
              this.isShowing = false;
              this.messageService.showError(err);
            }
          })
        },
        error: (err: HttpErrorResponse) => {
          this.isShowing = false;
          this.messageService.showError(err);
        }
      })
    } else {
    this.apiService.getUsableOrganizations().subscribe({
      next: (o) => {
        const localMap = new Map(Parameters.loggedUser.organizations.map(obj => [obj.organizationId, obj]));
        this.selectedUser.organizations = o.map(org => {
          // Controlla se esiste una versione aggiornata in locale
          if (localMap.has(org.organizationId)) {
            return { ...org, ...localMap.get(org.organizationId) }; 
            // Nota: usando lo spread operator {...objServer, ...locale} unisci le proprietà,
            // mantenendo quelle del server che non sono presenti nel locale.
          }
          return org; // Altrimenti restituisce l'oggetto originale del server
        });
        this.showDialog = true;
        this.isShowing = false;
      },
      error: (err: HttpErrorResponse) => {
        this.isShowing=false;
        this.messageService.showError(err);
      }
    });
    }
  }

  private loadData(): void {
    this.loading = true;

    this.apiService.getUsers().subscribe({
      next: (data) => {
        
        this.users = data.sort((a, b) => a.userId.localeCompare(b.userId, undefined, { numeric: true }));
        this.dtTrigger.next(true);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.messageService.showError(err);
      },
      complete: () => {
        this.loading = false;
      }
    })
  }

  private resetSelected(): void {
    this.newUser = Reset.user();
    this.selectedUser = Reset.user();
  }
}
